/**
 * Notifications Center: show only alerts whose field is currently active.
 *
 * The backend returns alerts for active fields only, but clients keep feed
 * items after the fetch (mobile persists them for months), so an alert can
 * outlive its field being archived. Feed items are therefore checked against
 * the field's CURRENT state in the loaded distribution tree.
 *
 * These helpers are CommonJS and dependency-free so they can be unit-tested
 * with `node --test` (see sciroot-webapp NotificationsCenter __tests__).
 */

const EMPTY_NOTIFICATIONS = [];

/**
 * A field counts as active unless its settings.field_state says otherwise.
 * Also guards alert-click navigation, where the full tree loads after the
 * alert was shown and may reveal the field as archived.
 */
const isFieldActive = field => {
  if (!field) {
    return false;
  }
  const fieldState = field.settings?.field_state;
  return !fieldState || fieldState === 'active';
};

/**
 * Keep an item unless its field is KNOWN and not active:
 * - distribution / farming unit missing, or a skeleton farming unit without
 *   `fields` → unknown, keep (the backend vouched for it when fetched);
 * - field missing from a loaded farming unit (deleted) → drop;
 * - field whose settings.field_state is set and not 'active' → drop.
 */
const filterNotificationsToActiveFields = (items, distributionsById) => {
  if (!items || items.length === 0) {
    return EMPTY_NOTIFICATIONS;
  }

  return items.filter(notification => {
    const farmingUnit =
      distributionsById?.[notification.distribution_id]?.farming_units?.[
        notification.farming_unit_id
      ];
    if (!farmingUnit?.fields) {
      return true;
    }

    return isFieldActive(farmingUnit.fields[notification.field_id]);
  });
};

const sameItems = (a, b) =>
  a.length === b.length && a.every((notification, i) => notification === b[i]);

let lastItems;
let lastDistributionsById;
let lastResult = EMPTY_NOTIFICATIONS;

/**
 * Ready-to-render Notifications Center feed. Memoized on the last feed items /
 * distributions references, and keeps the previous array when the filtered
 * contents are unchanged, so useSelector / useMemo consumers do not re-render
 * on unrelated store updates. Returns the raw items array when nothing is
 * hidden.
 */
const selectActiveFieldNotifications = state => {
  const items = state.authentication_data?.notifications_feed?.items;
  const distributionsById =
    state.distribution_data?.distributions?.distributions;

  if (items === lastItems && distributionsById === lastDistributionsById) {
    return lastResult;
  }

  let result = filterNotificationsToActiveFields(items, distributionsById);
  if (result.length === items?.length) {
    result = items;
  } else if (sameItems(result, lastResult)) {
    result = lastResult;
  }

  lastItems = items;
  lastDistributionsById = distributionsById;
  lastResult = result;
  return result;
};

module.exports = {
  isFieldActive,
  filterNotificationsToActiveFields,
  selectActiveFieldNotifications,
};
